import type { AxiosInstance, AxiosRequestConfig } from "axios";
import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type * as Paths from "./paths";

interface OperationMethods {
  "getBookings"(
    parameters?: Parameters<Paths.GetBookings.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetBookings.Responses.$200>;

  "createBooking"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateBooking.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateBooking.Responses.$200>;

  "getBooking"(
    parameters?: Parameters<
      Paths.GetBooking.QueryParameters & Paths.GetBooking.PathParameters
    > | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetBooking.Responses.$200>;

  "updateBooking"(
    parameters?: Parameters<Paths.UpdateBooking.PathParameters> | null,
    data?: Paths.UpdateBooking.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateBooking.Responses.$200>;

  "deleteBooking"(
    parameters?: Parameters<Paths.DeleteBooking.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteBooking.Responses.$204>;
}

export const getBookingOperations = (client: AxiosInstance) => {
  const getBookings: OperationMethods["getBookings"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/booking/booking";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const createBooking: OperationMethods["createBooking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/booking/booking";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const getBooking: OperationMethods["getBooking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/booking/booking/{booking}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const updateBooking: OperationMethods["updateBooking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/booking/booking/{booking}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };
  const deleteBooking: OperationMethods["deleteBooking"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/booking/booking/{booking}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  return {
    getBookings: getBookings,
    createBooking: createBooking,
    getBooking: getBooking,
    updateBooking: updateBooking,
    deleteBooking: deleteBooking,
  };
};
